import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Leaf, RefreshCw, ArrowLeft, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { computeTreemap } from "@/components/impact_map/treemap-utils";
import { HeatmapTile } from "@/components/impact_map/HeatmapTile";
import { ActionHoverCard } from "@/components/impact_map/ActionHoverCard";
import { TileDetail } from "@/components/impact_map/TileDetail";
import { InteractiveDoughnut } from "@/components/impact_map/InteractiveDoughnut";
import { PriorityQueue } from "@/components/impact_map/PriorityQueue";
import { ComparePanel } from "@/components/impact_map/ComparePanel";
import { Footer } from "@/components/Footer";
import { CompanyLogo } from "@/components/CompanyLogo";

export default function ImpactMapPage() {
  const { companyId } = useParams();
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState(null);

  const [actionsOpen, setActionsOpen] = useState(true);
  const [actionStatuses, setActionStatuses] = useState({});

  const [compareMode, setCompareMode] = useState(false);
  const [compareSlots, setCompareSlots] = useState([null, null, null]);
  const [draggedTile, setDraggedTile] = useState(null);

  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await apiGet(`/api/impact-nodes/${companyId}`, ctrl.signal);
        if (mounted) setData(res);
      } catch (err) {
        if (err.name === "AbortError") return;
        if (mounted) toast.error("Could not load impact data.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; ctrl.abort(); };
  }, [companyId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading]);

  const tiles = useMemo(() => {
    if (!data?.nodes || !dims.w) return [];
    return computeTreemap(data.nodes, dims.w, dims.h);
  }, [data, dims]);

  const handleMouseMove = useCallback((e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await apiPost(`/api/impact-nodes/${companyId}/regenerate`);
      setData(res);
      setActionStatuses({});
      toast.success("Impact data refreshed.");
    } catch (err) {
      toast.error("Could not regenerate.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusChange = useCallback((nodeId, status) => {
    setActionStatuses(prev => ({ ...prev, [nodeId]: status }));
  }, []);

  const handleFocusTile = useCallback((node) => {
    setSelected(node);
  }, []);

  const toggleCompareMode = useCallback(() => {
    setCompareMode(prev => {
      if (prev) setCompareSlots([null, null, null]);
      return !prev;
    });
  }, []);

  const handleCompareWith = useCallback((node) => {
    setCompareMode(true);
    setCompareSlots(prev => {
      const already = prev.some(s => s?.id === node.id);
      if (already) return prev;
      const firstEmpty = prev.findIndex(s => s === null);
      if (firstEmpty === -1) return prev;
      const next = [...prev];
      next[firstEmpty] = node;
      return next;
    });
  }, []);

  const handleCompareDragStart = useCallback((tile) => {
    setDraggedTile(tile);
  }, []);

  const handleCompareDrop = useCallback((e) => {
    e.preventDefault();
    if (!draggedTile) return;
    setCompareSlots(prev => {
      const already = prev.some(s => s?.id === draggedTile.id);
      if (already) return prev;
      const firstEmpty = prev.findIndex(s => s === null);
      if (firstEmpty === -1) return prev;
      const next = [...prev];
      next[firstEmpty] = draggedTile;
      return next;
    });
    setDraggedTile(null);
  }, [draggedTile]);

  const handleRemoveSlot = useCallback((index) => {
    setCompareSlots(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  }, []);

  const handleTileSelect = useCallback((tile) => {
    if (compareMode) {
      handleCompareWith(tile);
    } else {
      setSelected(tile);
    }
  }, [compareMode, handleCompareWith]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Analyzing impact data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" data-testid="impact-map-page">
      {/* Header */}
      <div className="shrink-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
          <button 
            onClick={() => {
              localStorage.removeItem('olana_company_id');
              localStorage.removeItem('olana_user_id');
              localStorage.removeItem('olana_email');
              window.location.href = '/';
            }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
            data-testid="logo-home-link"
          >
            <Leaf className="w-5 h-5 text-primary" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Olana</span>
          </button>
          <Separator orientation="vertical" className="h-5 bg-border/50" />
          {data?.company_name && (
            <>
              <CompanyLogo name={data.company_name} logoUrl={data.company_logo} />
              <span className="text-white font-semibold text-sm" data-testid="header-company-name">{data.company_name}</span>
              {data.company_country && <span className="text-white/40 text-xs">&middot; {data.company_country}</span>}
              <Separator orientation="vertical" className="h-5 bg-border/50" />
            </>
          )}
          <Link to={`/dashboard/${companyId}`} className="text-sm text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-dashboard">
            <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
          </Link>
          <span className="text-sm text-white font-medium">Impact Map</span>
          <div className="ml-auto flex items-center gap-3">
            <Button
              variant={compareMode ? "default" : "ghost"}
              size="sm"
              onClick={toggleCompareMode}
              className={`text-xs gap-1.5 ${compareMode ? "bg-primary/20 text-primary hover:bg-primary/30 border border-primary/30" : "text-muted-foreground hover:text-white"}`}
              data-testid="compare-mode-toggle"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              {compareMode ? "Exit Compare" : "Compare Mode"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating}
              className="text-muted-foreground hover:text-white text-xs gap-1.5" data-testid="regenerate-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} />
              {regenerating ? "Refreshing..." : "Refresh Data"}
            </Button>
          </div>
        </div>
      </div>

      {/* Content: Heatmap (left) + Doughnut (right) */}
      <div className="flex-1 flex min-h-0">
        {/* Heatmap area */}
        <div
          className="w-1/2 relative overflow-hidden"
          onMouseMove={handleMouseMove}
          onDragOver={compareMode ? (e) => e.preventDefault() : undefined}
          onDrop={compareMode ? handleCompareDrop : undefined}
        >
          <div ref={containerRef} className="absolute inset-3 md:inset-5" data-testid="heatmap-container">
            {tiles.map((tile) => (
              <HeatmapTile
                key={tile.id}
                tile={tile}
                dimmed={!!selected && selected.id !== tile.id}
                onSelect={handleTileSelect}
                onHoverStart={setHovered}
                onHoverEnd={() => setHovered(null)}
                compareMode={compareMode}
                onCompareDragStart={handleCompareDragStart}
              />
            ))}
          </div>

          <AnimatePresence>
            {hovered && !selected && !compareMode && <ActionHoverCard node={hovered} mousePos={mousePos} />}
          </AnimatePresence>

          {compareMode && (
            <div className="absolute inset-0 pointer-events-none z-0 border-2 border-dashed border-primary/10 rounded-xl m-2" />
          )}
        </div>

        {/* Doughnut panel (right — equal width) */}
        <div
          className="w-1/2 border-l border-white/[0.06] flex items-center justify-center p-4"
          style={{ background: "rgba(2,4,2,0.5)" }}
          data-testid="doughnut-panel"
        >
          <InteractiveDoughnut
            doughnut={data?.doughnut}
            hoveredNode={hovered}
            selectedNode={selected}
            allNodes={data?.nodes || []}
          />
        </div>
      </div>

      {/* Priority Queue (bottom pane) */}
      <div className="shrink-0">
        <PriorityQueue
          nodes={data?.nodes || []}
          actionStatuses={actionStatuses}
          onStatusChange={handleStatusChange}
          onFocusTile={handleFocusTile}
          onCompareWith={handleCompareWith}
          open={actionsOpen}
          onToggle={() => setActionsOpen(prev => !prev)}
        />
      </div>

      {/* Compare Panel (overlays bottom) */}
      <AnimatePresence>
        {compareMode && (
          <ComparePanel
            slots={compareSlots}
            onRemoveSlot={handleRemoveSlot}
            onClose={toggleCompareMode}
            allNodes={data?.nodes || []}
          />
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {selected && !compareMode && <TileDetail node={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>

      {/* Footer - pinned at bottom for full-height layout */}
      <div className="shrink-0 border-t border-border/30">
        <Footer />
      </div>
    </div>
  );
}
